import React, { useState, useEffect } from "react";
import PropTypes from "prop-types";
import Datepicker from "react-tailwindcss-datepicker";

export const DateRangePicker = ({ value = { startDate: null, endDate: null }, onChange, error = "", useRange = true, displayFormat = "DD/MM/YYYY", placeholder = "เลือกวันที่...", emptyLabel, minDate, maxDate }) => {
  const [dateValue, setDateValue] = useState({
    startDate: value?.startDate || null,
    endDate: value?.endDate || null,
  });

  // Update internal state when value prop changes
  useEffect(() => {
    if (value?.startDate || value?.endDate) {
      setDateValue({
        startDate: value.startDate || null,
        endDate: value.endDate || null,
      });
      return;
    }

    if (!value || (!value.startDate && !value.endDate)) {
      setDateValue({ startDate: null, endDate: null });
    }
  }, [value]);

  const handleChange = (newValue) => {
    if (!newValue || (!newValue.startDate && !newValue.endDate)) {
      setDateValue({ startDate: null, endDate: null });
      if (onChange) {
        onChange({ startDate: null, endDate: null });
      }
      return;
    }

    setDateValue(newValue);
    if (onChange) {
      onChange(newValue);
    }
  };

  const hasSelection = Boolean(dateValue?.startDate || dateValue?.endDate);
  const normalizedValue = hasSelection ? dateValue : null;
  const effectivePlaceholder = hasSelection ? placeholder : emptyLabel || placeholder;

  const errorMessage = React.useMemo(() => {
    if (!error) {
      return "";
    }

    if (typeof error === "string") {
      return error;
    }

    if (Array.isArray(error)) {
      return error.filter(Boolean).join(" ");
    }

    if (typeof error === "object") {
      return Object.values(error).filter(Boolean).join(" ");
    }

    return "";
  }, [error]);

  return (
    <div data-testid="date-range-picker" className="w-full">
      <Datepicker
        value={normalizedValue}
        onChange={handleChange}
        useRange={useRange}
        displayFormat={displayFormat}
        inputClassName="form-control"
        containerClassName="relative"
        toggleClassName="absolute right-0 h-full px-3 text-gray-700 focus:outline-none"
        popoverDirection="down"
        placeholder={effectivePlaceholder}
        primaryColor="blue"
        minDate={minDate}
        maxDate={maxDate}
        configs={{
          shortcuts: {
            today: "วันนี้",
            yesterday: "เมื่อวาน",
            past: (period) => `${period} วันที่ผ่านมา`,
            currentMonth: "เดือนนี้",
            pastMonth: "เดือนที่แล้ว",
          },
          footer: {
            cancel: "ยกเลิก",
            apply: "ตกลง",
          },
        }}
      />
      {errorMessage && <div className="text-red-500 text-sm mt-1">{errorMessage}</div>}
    </div>
  );
};

DateRangePicker.propTypes = {
  value: PropTypes.oneOfType([
    PropTypes.shape({
      startDate: PropTypes.oneOfType([PropTypes.instanceOf(Date), PropTypes.string, PropTypes.number]),
      endDate: PropTypes.oneOfType([PropTypes.instanceOf(Date), PropTypes.string, PropTypes.number]),
    }),
  ]),
  onChange: PropTypes.func.isRequired,
  error: PropTypes.oneOfType([PropTypes.string, PropTypes.array, PropTypes.object]),
  useRange: PropTypes.bool,
  displayFormat: PropTypes.string,
  placeholder: PropTypes.string,
  emptyLabel: PropTypes.string,
  minDate: PropTypes.instanceOf(Date),
  maxDate: PropTypes.instanceOf(Date),
};

export default DateRangePicker;
